import {
  useEffect, useId, useMemo, useRef, useState, type KeyboardEvent, type ReactNode,
} from 'react'
import { IconCheck, IconChevronDown, IconX } from '@tabler/icons-react'
import { FieldShell, type FieldShellProps } from './Field'
import { cx } from './primitives'

export interface ComboOption {
  value: string
  label: string
  group?: string
  disabled?: boolean
  /** Seçenek satırında etiketten önce çizilir (avatar, renk noktası…). */
  icon?: ReactNode
  /** Arama için ek metin (ör. kullanıcı adı). */
  keywords?: string
}

const norm = (s: string) => s.toLocaleLowerCase('tr').normalize('NFD').replace(/\p{Diacritic}/gu, '')

function useOutside(ref: React.RefObject<HTMLElement>, onOut: () => void, active: boolean) {
  useEffect(() => {
    if (!active) return
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) onOut() }
    document.addEventListener('mousedown', h)
    return () => document.removeEventListener('mousedown', h)
  }, [ref, onOut, active])
}

interface ListProps {
  id: string
  options: ComboOption[]
  active: number
  isSelected: (v: string) => boolean
  onPick: (o: ComboOption) => void
  onHover: (i: number) => void
  empty: ReactNode
}

function OptionList({ id, options, active, isSelected, onPick, onHover, empty }: ListProps) {
  let lastGroup: string | undefined
  return (
    <div id={id} role="listbox" className="ui-listbox">
      {options.length === 0 && <div className="ui-listbox__empty">{empty}</div>}
      {options.map((o, i) => {
        const head = o.group && o.group !== lastGroup ? o.group : null
        lastGroup = o.group
        return (
          <div key={o.value}>
            {head && <div className="ui-listbox__group" role="presentation">{head}</div>}
            <div id={`${id}-o${i}`} role="option" aria-selected={isSelected(o.value)} aria-disabled={o.disabled || undefined}
              data-active={i === active} className="ui-option"
              onMouseDown={(e) => { e.preventDefault(); if (!o.disabled) onPick(o) }} onMouseEnter={() => onHover(i)}>
              {o.icon}
              <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.label}</span>
              {isSelected(o.value) && <IconCheck size={15} className="ui-option__check" aria-hidden="true" />}
            </div>
          </div>
        )
      })}
    </div>
  )
}

function useListKeys(count: number, onEnter: (i: number) => void, close: () => void, open: () => void, isOpen: boolean) {
  const [active, setActive] = useState(0)
  useEffect(() => { if (active >= count) setActive(Math.max(0, count - 1)) }, [count, active])
  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); if (!isOpen) open(); else setActive((a) => Math.min(count - 1, a + 1)) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(0, a - 1)) }
    else if (e.key === 'Enter') { if (isOpen && count > 0) { e.preventDefault(); onEnter(active) } }
    else if (e.key === 'Escape') { if (isOpen) { e.preventDefault(); e.stopPropagation(); close() } }
  }
  return { active, setActive, onKeyDown }
}

export interface SelectProps extends FieldShellProps {
  data: ComboOption[]
  value: string | null
  onChange: (value: string | null) => void
  placeholder?: string
  searchable?: boolean
  clearable?: boolean
  disabled?: boolean
  nothingFound?: ReactNode
  /** Seçili değerin solunda (renk noktası, simge). */
  leftSection?: ReactNode
  size?: 'sm' | 'md'
  'aria-label'?: string
  id?: string
}

/** Tek seçim. role="combobox" + listbox; aranabilir ya da salt seçim. */
export function Select({
  data, value, onChange, placeholder, searchable, clearable, disabled, nothingFound = 'Sonuç yok', leftSection,
  size = 'md', label, description, error, required, className, id, ...aria
}: SelectProps) {
  const auto = useId()
  const fid = id ?? auto
  const listId = `${fid}-list`
  const wrap = useRef<HTMLDivElement>(null)
  const [isOpen, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const selected = data.find((o) => o.value === value) ?? null
  const shown = useMemo(() => {
    if (!searchable || !query) return data
    const q = norm(query)
    return data.filter((o) => norm(o.label + ' ' + (o.keywords ?? '')).includes(q))
  }, [data, query, searchable])
  const close = () => { setOpen(false); setQuery('') }
  useOutside(wrap, close, isOpen)
  const pick = (o: ComboOption) => { onChange(o.value); close() }
  const keys = useListKeys(shown.length, (i) => shown[i] && !shown[i].disabled && pick(shown[i]), close, () => setOpen(true), isOpen)

  return (
    <FieldShell id={fid} label={label} description={description} error={error} required={required} className={className}>
      <div ref={wrap} className="ui-combo">
        <div className={cx('ui-control', 'ui-combo__control', 'ui-combo__control--single', size === 'sm' && 'ui-control--sm', error && 'ui-control--error', disabled && 'ui-control--disabled')}
          onMouseDown={(e) => { if (disabled) return; if ((e.target as HTMLElement).tagName !== 'INPUT') e.preventDefault(); setOpen((o) => !o) }}>
          {selected?.icon ?? leftSection}
          <input id={fid} role="combobox" aria-expanded={isOpen} aria-controls={listId} aria-autocomplete={searchable ? 'list' : 'none'}
            aria-activedescendant={isOpen && shown[keys.active] ? `${listId}-o${keys.active}` : undefined}
            aria-label={aria['aria-label']} aria-invalid={error ? true : undefined} required={required}
            className="ui-combo__input" disabled={disabled} readOnly={!searchable}
            value={isOpen && searchable ? query : (selected?.label ?? '')}
            placeholder={selected ? selected.label : placeholder}
            onChange={(e) => { setQuery(e.currentTarget.value); setOpen(true) }}
            onKeyDown={keys.onKeyDown} style={{ cursor: searchable ? 'text' : 'pointer' }} />
          {clearable && selected && !disabled ? (
            <button type="button" className="ui-combo__tag-x" aria-label="Seçimi temizle"
              onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); onChange(null) }}><IconX size={14} /></button>
          ) : (
            <IconChevronDown size={15} className="ui-combo__caret" aria-hidden="true" />
          )}
        </div>
        {isOpen && (
          <OptionList id={listId} options={shown} active={keys.active} isSelected={(v) => v === value}
            onPick={pick} onHover={keys.setActive} empty={nothingFound} />
        )}
      </div>
    </FieldShell>
  )
}

export interface MultiSelectProps extends FieldShellProps {
  data: ComboOption[]
  value: string[]
  onChange: (value: string[]) => void
  placeholder?: string
  searchable?: boolean
  clearable?: boolean
  disabled?: boolean
  nothingFound?: ReactNode
  maxValues?: number
  size?: 'sm' | 'md'
  'aria-label'?: string
  id?: string
  /** Seçili etiketin içeriği (avatar + ad). */
  renderTag?: (o: ComboOption) => ReactNode
}

/** Çoklu seçim: seçilenler etiket, liste açık kalır; Backspace son etiketi siler. */
export function MultiSelect({
  data, value, onChange, placeholder, searchable = true, clearable, disabled, nothingFound = 'Sonuç yok', maxValues,
  size = 'md', label, description, error, required, className, id, renderTag, ...aria
}: MultiSelectProps) {
  const auto = useId()
  const fid = id ?? auto
  const listId = `${fid}-list`
  const wrap = useRef<HTMLDivElement>(null)
  const input = useRef<HTMLInputElement>(null)
  const [isOpen, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const chosen = value.map((v) => data.find((o) => o.value === v)).filter(Boolean) as ComboOption[]
  const shown = useMemo(() => {
    const q = norm(query)
    return data.filter((o) => !q || norm(o.label + ' ' + (o.keywords ?? '')).includes(q))
  }, [data, query])
  const close = () => { setOpen(false); setQuery('') }
  useOutside(wrap, close, isOpen)
  const full = maxValues !== undefined && value.length >= maxValues
  const toggle = (o: ComboOption) => {
    if (value.includes(o.value)) onChange(value.filter((v) => v !== o.value))
    else if (!full) onChange([...value, o.value])
    setQuery('')
  }
  const keys = useListKeys(shown.length, (i) => shown[i] && !shown[i].disabled && toggle(shown[i]), close, () => setOpen(true), isOpen)

  return (
    <FieldShell id={fid} label={label} description={description} error={error} required={required} className={className}>
      <div ref={wrap} className="ui-combo">
        <div className={cx('ui-control', 'ui-combo__control', size === 'sm' && 'ui-control--sm', error && 'ui-control--error', disabled && 'ui-control--disabled')}
          onMouseDown={(e) => { if (disabled) return; if ((e.target as HTMLElement).tagName !== 'INPUT') e.preventDefault(); setOpen(true); input.current?.focus() }}>
          {chosen.map((o) => (
            <span key={o.value} className="ui-combo__tag">
              {renderTag ? renderTag(o) : <>{o.icon}{o.label}</>}
              {!disabled && (
                <button type="button" className="ui-combo__tag-x" aria-label={`${o.label} kaldır`}
                  onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); onChange(value.filter((v) => v !== o.value)) }}>
                  <IconX size={12} />
                </button>
              )}
            </span>
          ))}
          <input ref={input} id={fid} role="combobox" aria-expanded={isOpen} aria-controls={listId} aria-autocomplete="list"
            aria-activedescendant={isOpen && shown[keys.active] ? `${listId}-o${keys.active}` : undefined}
            aria-label={aria['aria-label']} aria-invalid={error ? true : undefined}
            className="ui-combo__input" disabled={disabled} readOnly={!searchable}
            value={query} placeholder={chosen.length ? '' : placeholder}
            onChange={(e) => { setQuery(e.currentTarget.value); setOpen(true) }}
            onKeyDown={(e) => {
              if (e.key === 'Backspace' && !query && value.length) onChange(value.slice(0, -1))
              keys.onKeyDown(e)
            }} />
          {clearable && value.length > 0 && !disabled && (
            <button type="button" className="ui-combo__tag-x ui-combo__clear" aria-label="Tümünü temizle"
              onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); onChange([]) }}><IconX size={14} /></button>
          )}
        </div>
        {isOpen && (
          <OptionList id={listId} options={shown} active={keys.active} isSelected={(v) => value.includes(v)}
            onPick={toggle} onHover={keys.setActive} empty={nothingFound} />
        )}
      </div>
    </FieldShell>
  )
}

export interface TagsInputProps extends FieldShellProps {
  value: string[]
  onChange: (value: string[]) => void
  placeholder?: string
  maxTags?: number
  suggestions?: string[]
  disabled?: boolean
  'aria-label'?: string
  id?: string
}

/** Serbest etiket girişi: Enter ya da virgül ekler, Backspace son etiketi siler. */
export function TagsInput({
  value, onChange, placeholder, maxTags, suggestions = [], disabled, label, description, error, required, className, id, ...aria
}: TagsInputProps) {
  const auto = useId()
  const fid = id ?? auto
  const listId = `${fid}-list`
  const wrap = useRef<HTMLDivElement>(null)
  const input = useRef<HTMLInputElement>(null)
  const [query, setQuery] = useState('')
  const [isOpen, setOpen] = useState(false)
  const full = maxTags !== undefined && value.length >= maxTags
  const add = (raw: string) => {
    const t = raw.trim()
    if (!t || full || value.some((v) => v.toLocaleLowerCase('tr') === t.toLocaleLowerCase('tr'))) { setQuery(''); return }
    onChange([...value, t]); setQuery('')
  }
  const sugg = suggestions.filter((s) => !value.includes(s) && (!query || norm(s).includes(norm(query)))).slice(0, 8)
    .map((s) => ({ value: s, label: s }))
  useOutside(wrap, () => setOpen(false), isOpen)
  const keys = useListKeys(sugg.length, (i) => sugg[i] && add(sugg[i].value), () => setOpen(false), () => setOpen(true), isOpen && sugg.length > 0)

  return (
    <FieldShell id={fid} label={label} description={description} error={error} required={required} className={className}>
      <div ref={wrap} className="ui-combo">
        <div className={cx('ui-control', 'ui-combo__control', error && 'ui-control--error', disabled && 'ui-control--disabled')}
          onMouseDown={(e) => { if ((e.target as HTMLElement).tagName !== 'INPUT') e.preventDefault(); input.current?.focus() }}>
          {value.map((t) => (
            <span key={t} className="ui-combo__tag">
              {t}
              {!disabled && (
                <button type="button" className="ui-combo__tag-x" aria-label={`${t} etiketini kaldır`}
                  onMouseDown={(e) => { e.preventDefault(); onChange(value.filter((v) => v !== t)) }}><IconX size={12} /></button>
              )}
            </span>
          ))}
          <input ref={input} id={fid} className="ui-combo__input" disabled={disabled || full}
            role={sugg.length ? 'combobox' : undefined} aria-expanded={sugg.length ? isOpen : undefined} aria-controls={sugg.length ? listId : undefined}
            aria-label={aria['aria-label']} value={query} placeholder={value.length ? '' : placeholder}
            onFocus={() => setOpen(true)}
            onChange={(e) => {
              const v = e.currentTarget.value
              if (v.endsWith(',')) add(v.slice(0, -1)); else { setQuery(v); setOpen(true) }
            }}
            onBlur={() => { if (query) add(query) }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !(isOpen && sugg.length)) { e.preventDefault(); add(query) }
              else if (e.key === 'Backspace' && !query && value.length) onChange(value.slice(0, -1))
              else keys.onKeyDown(e)
            }} />
        </div>
        {isOpen && sugg.length > 0 && (
          <OptionList id={listId} options={sugg} active={keys.active} isSelected={() => false}
            onPick={(o) => add(o.value)} onHover={keys.setActive} empty="" />
        )}
      </div>
    </FieldShell>
  )
}
